import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Bookmark, Search, X } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { DealCard } from '../components/common/DealCard';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const BookmarksPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookmarkIds, setBookmarkIds] = useState([]);

  useEffect(() => {
    const ids = JSON.parse(localStorage.getItem('bookmarks') || '[]');
    setBookmarkIds(ids);
    if (ids.length > 0) {
      fetchBookmarkedDeals(ids);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchBookmarkedDeals = async (ids) => {
    try {
      const res = await axios.get(`${API}/api/deals`);
      const bookmarked = res.data.filter(deal => ids.includes(deal.id));
      setDeals(bookmarked);
    } catch (e) { toast.error('Failed to load bookmarks'); }
    setLoading(false);
  };

  const removeBookmark = (dealId) => {
    const newIds = bookmarkIds.filter(id => id !== dealId);
    localStorage.setItem('bookmarks', JSON.stringify(newIds));
    setBookmarkIds(newIds);
    setDeals(deals.filter(d => d.id !== dealId));
    toast.success('Removed from saved deals');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bookmarks-page">
      <SEO title="Saved Deals - JustOffs | Your Bookmarks" description="View and manage your saved deals on JustOffs. Keep track of the best local offers you've found." />
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Bookmark className="w-6 h-6 text-slate-600" />
          <h1 className="text-2xl font-bold text-slate-800">Saved Deals</h1>
          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-sm font-medium">{deals.length}</span>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div></div>
        ) : deals.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="bookmarks-grid">
            {deals.map(deal => (
              <div key={deal.id} className="relative">
                <DealCard deal={deal} />
                <button onClick={() => removeBookmark(deal.id)} className="absolute top-2 right-2 bg-white p-1.5 rounded-full shadow-md hover:bg-red-50 transition" title="Remove" data-testid={`remove-bookmark-${deal.id}`}>
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl">
            <Bookmark className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No saved deals yet</h3>
            <p className="text-slate-500 mb-6">Browse deals and tap the bookmark icon to save them here</p>
            <Link to="/deals" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition" data-testid="browse-deals-btn">
              <Search className="w-5 h-5" /> Browse Deals
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
